import { useState, useEffect } from 'react';
import { Star, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    rating: 5,
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews/${productId}`),
        axios.get(`${API}/reviews/stats/${productId}`)
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.comment) {
      toast.error('Preencha todos os campos');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        customer_name: formData.customer_name,
        rating: formData.rating,
        comment: formData.comment
      });
      
      toast.success('Avaliação enviada com sucesso!');
      setFormData({ customer_name: '', rating: 5, comment: '' });
      setShowForm(false);
      await fetchReviews();
    } catch (error) {
      toast.error('Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onStarClick = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onStarClick && onStarClick(star)}
            disabled={!interactive}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : ''} transition-transform`}
            data-testid={`star-${star}`}
          >
            <Star
              className={`h-5 w-5 ${star <= rating ? 'fill-primary text-primary' : 'text-zinc-600'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="mt-16 pt-12 border-t border-white/10" data-testid="review-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl font-bold text-white mb-2" data-testid="reviews-title">
            Avaliações
          </h2>
          {stats.total_reviews > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {renderStars(Math.round(stats.average_rating))}
              </div>
              <span className="text-zinc-400" data-testid="review-stats">
                {stats.average_rating} ({stats.total_reviews} {stats.total_reviews === 1 ? 'avaliação' : 'avaliações'})
              </span>
            </div>
          )}
        </div>
        
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition-colors text-sm"
          data-testid="add-review-button"
        >
          {showForm ? 'Cancelar' : 'Escrever Avaliação'}
        </button>
      </div>

      {/* Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <form onSubmit={handleSubmit} className="bg-zinc-900/50 border border-white/10 p-6" data-testid="review-form">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">
                    Seu Nome
                  </label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 text-white"
                    required
                    data-testid="review-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">
                    Avaliação
                  </label>
                  {renderStars(formData.rating, true, (star) => setFormData({ ...formData, rating: star }))}
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2">
                    Seu Comentário
                  </label>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    rows={4}
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary p-4 text-white resize-none"
                    required
                    data-testid="review-comment-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors disabled:opacity-50"
                  data-testid="submit-review-button"
                >
                  {submitting ? 'Enviando...' : 'Enviar Avaliação'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8" data-testid="reviews-loading">
          <div className="inline-block h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/30 border border-white/5" data-testid="no-reviews">
          <p className="text-zinc-400">Seja o primeiro a avaliar este produto!</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="reviews-list">
          {reviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/30 border border-white/5 p-6"
              data-testid={`review-${review.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="bg-zinc-800 p-3 rounded-full">
                  <User className="h-6 w-6 text-zinc-400" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-heading font-bold text-white" data-testid="review-author">
                      {review.customer_name}
                    </h4>
                    <span className="text-xs text-zinc-500" data-testid="review-date">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    {renderStars(review.rating)}
                  </div>
                  
                  <p className="text-zinc-300 leading-relaxed" data-testid="review-comment">
                    {review.comment}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;